import type { ChatMessage, ToolArtifact } from "@models/chat";
import { create } from "zustand";

export type ActivityKind = "thinking" | "tool_call" | "tool_result";

/**
 * Claude model the user has chosen for the next chat turn. Pinned
 * server-side on the first message of a conversation; subsequent turns
 * ignore the picker (the chat input disables it once `messages.length >
 * 0` to mirror this). Defaults to "sonnet" — every new chat resets to
 * Sonnet, deliberately. Last-used isn't persisted: a returning Opus
 * user would otherwise rack up 5× cost on a follow-up they didn't
 * intend to be Opus.
 */
export type ChatModel = "sonnet" | "opus";

export interface ActivityEntry {
  kind: ActivityKind;
  /** For "tool_call"/"tool_result": the tool name. For "thinking": the prose. */
  label: string;
  /** For "tool_call": the argument dict we streamed. */
  args?: Record<string, unknown>;
  /**
   * For "tool_result": a one-line summary of what the tool returned
   * (UX-1). Empty / undefined when the backend couldn't extract a
   * meaningful summary; the activity row falls back to "{name} done".
   */
  summary?: string;
  /**
   * Stable identity assigned by `pushActivity`. Used as the React key
   * by the streaming activity timeline so re-renders don't churn rows.
   * Monotonic per process — collisions are not a concern within a turn.
   */
  id?: number;
}

interface ChatState {
  messages: ChatMessage[];
  sessionId: string | null;
  sessionTitle: string;
  /**
   * Subset of board UUIDs the user has scoped this chat to.
   *
   *   - [] (default) — whole project; agent reasons across every
   *     member board, sees every connector / brief block in its
   *     system context.
   *   - [bid] — single board pin.
   *   - [bid_a, bid_b] — explicit subset.
   *
   * Sent on every `/chat` POST as `board_ids`. Server stamps it
   * onto session state as `active_board_ids` and the agent's tool
   * fan-out (`tools/_shared._active_boards`) filters by it.
   *
   * Independent from `useProjectStore.activeBoardId` (the
   * navigational PCB the user is *looking at* — sidebar / URL).
   * The chat scope is its own thing, set explicitly via the
   * composer's scope chip, so navigating between board pages
   * doesn't silently change the agent's context.
   */
  activeChatBoardIds: string[];
  isStreaming: boolean;
  streamingContent: string;
  /** Live "what is the agent doing" log for the current turn only. */
  activity: ActivityEntry[];
  /**
   * Artifacts the agent has emitted DURING the current streaming turn.
   * Populated as `tool_result` SSE events arrive carrying an
   * `artifacts` payload — without this, charts/schematics rendered
   * inline only after the turn finished and the persisted message
   * hydrated from `/sessions/<id>/messages`, leaving the user staring
   * at prose that discusses a chart they can't yet see for ~30s+ on
   * multi-tool turns.
   *
   * Cleared at the start of every new turn (alongside `activity` and
   * `streamingContent`) — the post-`done` hydration replaces the
   * StreamingMessage with the real ChatMessage which has the same
   * artifacts on `tool_calls[].artifacts`, so this is purely a
   * mid-turn bridge.
   */
  liveArtifacts: ToolArtifact[];
  /**
   * Transient reassurance message shown over the activity row for a few
   * seconds at a time when a turn is taking unusually long. Null when no
   * flash is active — in that case the activity-row renders the latest
   * entry from `activity` as normal. Set by a minute-interval timer in
   * useChat that fires once the turn has been running for ≥3 minutes,
   * cleared automatically 3 seconds later so the activity feed takes
   * back over.
   */
  transientStatus: string | null;
  /**
   * Pagination cursor: the oldest event timestamp currently in `messages`.
   * Passed back as `before_ts` when fetching the next-older page. Null
   * when the store is empty or the loaded slice already reaches the
   * beginning of the session.
   */
  oldestTimestamp: number | null;
  /** True when older history exists beyond the loaded slice. */
  hasMoreOlder: boolean;
  /** True while a "load older" fetch is in flight (drives the spinner). */
  isLoadingOlder: boolean;
  /**
   * True from the moment the user clicks a session in the sidebar until
   * its message page lands. The sidebar previously owned this state
   * locally and rendered "Loading…" *over the row title* — confusing
   * because the user's attention is on the chat window, not the row
   * they just clicked. Surfacing it on the store lets the chat window
   * render a skeleton during the fetch so the click feels instant
   * regardless of network latency.
   */
  isHydrating: boolean;
  /**
   * True while we're checking whether a switched-into session has an
   * active server-side run, and during the brief reattach handshake
   * before any real SSE event arrives. Surfaced by ChatContainer as a
   * thin top banner — the previous "Reconnecting…" placeholder was
   * pushed onto the activity feed which made StreamingMessage render
   * a fake assistant reply ("Circuit Forge: Reconnecting…"). Cleared
   * automatically once real activity flows or the recovery returns.
   */
  isReconnecting: boolean;
  /** Model to send on the next chat POST. See `ChatModel` for the
   * lifecycle (pinned server-side on first turn; reset to "sonnet"
   * by `clearMessages` when starting a new conversation). */
  selectedModel: ChatModel;
  /**
   * Sticky payload set when the most recent turn was rejected with
   * `insufficient_credits`. Drives the `<OutOfCreditsCard />` rendered
   * just above the chat composer. Cleared by the next user send (so a
   * top-up + retry flow naturally hides the card) or by an explicit
   * dismiss action.
   */
  insufficientCredits: { required: number; available: number } | null;
  /**
   * Queued composer text — set by features that "ask the agent to do X"
   * from outside the chat (the DRC AskAgentToFixButton, future
   * BOM-row "find a substitute" gestures, etc.). The ChatInput watches
   * `composerDraftSeq` so the prefill fires even when the same text is
   * queued twice.
   *
   * Cleared by ChatInput once it copies the value into its local
   * editing state — this keeps `composerDraft` a one-shot instruction
   * channel rather than a piece of mirrored state the input has to
   * keep in sync with.
   */
  composerDraft: string;
  composerDraftSeq: number;
}

let _activityIdSeq = 0;
function nextActivityId(): number {
  _activityIdSeq += 1;
  return _activityIdSeq;
}

function shallowEqualArgs(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  if (a === b) return true;
  const ka = a ? Object.keys(a) : [];
  const kb = b ? Object.keys(b) : [];
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.is(a?.[k], b?.[k])) return false;
  }
  return true;
}

interface ChatActions {
  setSessionId: (id: string | null) => void;
  setSessionTitle: (title: string) => void;
  setActiveChatBoardIds: (ids: string[]) => void;
  addMessage: (msg: ChatMessage) => void;
  /**
   * Replace the message list with a freshly-loaded page. Optionally
   * accepts the page's pagination metadata (`hasMoreOlder`,
   * `oldestTimestamp`); pass them when hydrating from the paginated
   * `/messages` endpoint so the "Load older" affordance can render.
   */
  setMessages: (
    msgs: ChatMessage[],
    pagination?: { hasMoreOlder?: boolean; oldestTimestamp?: number | null },
  ) => void;
  /**
   * Prepend an older slice in front of the current message list and
   * advance the pagination cursor. Used by the "Load older" button
   * after fetching the next-older page.
   */
  prependMessages: (
    msgs: ChatMessage[],
    pagination: { hasMoreOlder: boolean; oldestTimestamp: number | null },
  ) => void;
  clearMessages: () => void;
  setStreaming: (streaming: boolean) => void;
  setLoadingOlder: (loading: boolean) => void;
  setHydrating: (hydrating: boolean) => void;
  setReconnecting: (reconnecting: boolean) => void;
  setSelectedModel: (model: ChatModel) => void;
  setInsufficientCredits: (
    payload: { required: number; available: number } | null,
  ) => void;
  appendStreamChunk: (chunk: string) => void;
  resetStreamingContent: () => void;
  pushActivity: (entry: ActivityEntry) => void;
  pushLiveArtifact: (artifact: ToolArtifact) => void;
  setTransientStatus: (text: string | null) => void;
  /** Inject text into the chat composer. The input picks it up via an
   *  effect on `composerDraftSeq`. */
  prefillComposer: (text: string) => void;
  clearComposerDraft: () => void;
}

export const useChatStore = create<ChatState & ChatActions>()((set) => ({
  messages: [],
  sessionId: null,
  sessionTitle: "",
  isStreaming: false,
  streamingContent: "",
  activity: [],
  liveArtifacts: [],
  transientStatus: null,
  oldestTimestamp: null,
  hasMoreOlder: false,
  isLoadingOlder: false,
  isHydrating: false,
  isReconnecting: false,
  selectedModel: "sonnet",
  insufficientCredits: null,
  composerDraft: "",
  composerDraftSeq: 0,
  activeChatBoardIds: [],

  setSessionId: (id) => set({ sessionId: id }),
  setSessionTitle: (title) => set({ sessionTitle: title }),
  setActiveChatBoardIds: (ids) =>
    set({ activeChatBoardIds: Array.from(new Set(ids)) }),
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  setMessages: (msgs, pagination) =>
    set({
      messages: msgs,
      // Pagination metadata is optional so live-stream callers (which
      // produce a single locally-built message at a time) don't have to
      // reason about it. When omitted we leave the cursor untouched and
      // assume there's no older slice — the page-hydration callers pass
      // the real values.
      hasMoreOlder: pagination?.hasMoreOlder ?? false,
      oldestTimestamp: pagination?.oldestTimestamp ?? null,
    }),
  prependMessages: (msgs, pagination) =>
    set((s) => ({
      messages: [...msgs, ...s.messages],
      hasMoreOlder: pagination.hasMoreOlder,
      oldestTimestamp: pagination.oldestTimestamp,
    })),
  clearMessages: () =>
    set({
      messages: [],
      sessionId: null,
      sessionTitle: "",
      oldestTimestamp: null,
      hasMoreOlder: false,
      isLoadingOlder: false,
      isReconnecting: false,
      // Leaving the session invalidates any in-flight loadSessionById
      // hydration — its finally block is guarded by sessionId === sid
      // and won't fire once we null sessionId, so reset here to avoid
      // a stuck skeleton on the next welcome screen.
      isHydrating: false,
      // Each new chat starts on Sonnet. Deliberately not last-used:
      // a returning Opus user shouldn't accidentally rack up 5× cost
      // on a follow-up they didn't intend to be Opus — they have to
      // re-pick it for every conversation.
      selectedModel: "sonnet",
      insufficientCredits: null,
    }),
  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setLoadingOlder: (loading) => set({ isLoadingOlder: loading }),
  setHydrating: (hydrating) => set({ isHydrating: hydrating }),
  setReconnecting: (reconnecting) => set({ isReconnecting: reconnecting }),
  setSelectedModel: (model) => set({ selectedModel: model }),
  setInsufficientCredits: (payload) => set({ insufficientCredits: payload }),
  appendStreamChunk: (chunk) =>
    set((s) => ({ streamingContent: s.streamingContent + chunk })),
  resetStreamingContent: () =>
    set({
      streamingContent: "",
      activity: [],
      liveArtifacts: [],
      transientStatus: null,
    }),
  setTransientStatus: (text) => set({ transientStatus: text }),
  pushActivity: (entry) =>
    set((s) => {
      // Collapse consecutive identical lines so repeated emits from the
      // agent don't spam the log.
      const last = s.activity[s.activity.length - 1];
      if (
        last &&
        last.kind === entry.kind &&
        last.label === entry.label &&
        shallowEqualArgs(last.args, entry.args)
      ) {
        return s;
      }
      // Stamp an id if the caller didn't supply one. Stable across
      // re-renders so React keys stay anchored to the same DOM nodes.
      const stamped: ActivityEntry =
        entry.id === undefined ? { ...entry, id: nextActivityId() } : entry;
      // Cap the log to avoid unbounded growth on tool-heavy turns. The
      // earlier 20-entry cap silently truncated the early reasoning on
      // long turns: a design-review fan-out routinely hits 30+ tool
      // calls, which left the user staring at "tool 21..40" with no
      // record of the orientation steps the agent took first. 200 is
      // comfortably above any single observed turn while keeping the
      // store cheap (each entry is a small dict, ≈ a few hundred
      // bytes — 200 entries is well under 100 KB even in the worst
      // case). If we ever need more, the right answer is server-side
      // pagination via the activity feed, not a higher in-memory cap.
      const next = [...s.activity, stamped];
      if (next.length > 200) next.splice(0, next.length - 200);
      return { activity: next };
    }),
  pushLiveArtifact: (artifact) =>
    set((s) => {
      // De-dupe on URL — the same tool_result SSE can arrive twice in
      // unusual reattach scenarios, and we'd rather surface one chart
      // than two stacked copies.
      if (s.liveArtifacts.some((a) => a.url === artifact.url)) {
        return s;
      }
      return { liveArtifacts: [...s.liveArtifacts, artifact] };
    }),
  prefillComposer: (text) =>
    set((s) => ({
      composerDraft: text,
      composerDraftSeq: s.composerDraftSeq + 1,
    })),
  clearComposerDraft: () => set({ composerDraft: "" }),
}));
